import { memo, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react'

import { IPartner } from 'clickNgo/entities/partner/models'
import { formatErrorMessages } from 'clickNgo/helpers/api'
import { InfoBlock, InfoBlockType } from 'cng-common/components/infoBlock'
import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { useGetFormData } from 'cng-common/features/form'
import { validateFormData } from 'cng-common/features/form/helpers'
import { Translation } from 'cng-common/features/translations/components/translation'
import { Button } from 'core-components/library/button'
import { Tooltip } from 'core-components/library/tooltip'
import { ConfirmDialog } from 'shared/components/confirmDialog'

import { PARTNER_CONTAINER_HEADER_HEIGHT, UpdatePartnerResult } from '../../configs'
import { useEditMenuItems } from '../../hooks/useEditMenuItems'
import { useReadOnlyMenuItem } from '../../hooks/useReadOnlyMenuItem'
import { partnerEditorTranslation } from '../../translation'
import { PartnerFormTitle } from '../partnerFormTitle/PartnerFormTitle'
import { PartnerInactivePanel } from '../partnerInactivatePanel/PartnerInactivatePanel'
import { PartnerWorkflowStatusPanel } from '../partnerWorkflowStatusPanel/PartnerWorkflowStatusPanel'

import { useInactivatePartner } from './hooks/useInactivatePartner'
import { useMandatoryAttachmentsForm } from './hooks/useMandatoryAttachmentsForm'
import { IPartnerFormEditableContainerProps } from './models'

import styles from './PartnerFormEditableContainer.module.scss'

export const PartnerFormEditableContainer = memo<
  PropsWithChildren<IPartnerFormEditableContainerProps>
>(
  ({
    onApply,
    onCancel,
    formKey,
    partner,
    title,
    children,
    errorMessages = [],
    isAttachmentRequired,
    isDisabled,
  }) => {
    const [isLoading, setIsLoading] = useState(false)
    const [isUpdateSuccessful, setIsUpdateSuccessful] = useState(false)
    const [isRequestCreated, setIsRequestCreated] = useState(false)
    const [isInactivatePartnerDialogVisible, setIsInactivatePartnerDialogVisible] = useState(false)
    const [partnerFormData] = useGetFormData<IPartner>(formKey)
    const {
      isCurrentMenuItemEditable,
      isWorkflowStatusReadOnly,
      setIsCurrentMenuItemEditable,
      currentMenuItem,
    } = useEditMenuItems(partner)
    const {
      isInactive,
      inactivatePartner,
      isPartnerInactivationPossible,
      partnerInactivationImpossibleMessage,
      partnerInactivationErrorMessages,
    } = useInactivatePartner()

    const { isReadOnly } = useReadOnlyMenuItem()

    const formattedErrorMessages = useMemo(
      () => formatErrorMessages(errorMessages),
      [errorMessages]
    )

    const formattedInactivationErrorMessages = useMemo(
      () => formatErrorMessages(partnerInactivationErrorMessages),
      [partnerInactivationErrorMessages]
    )

    const applyChanges = useCallback(async () => {
      if (onApply) {
        setIsUpdateSuccessful(false)
        setIsRequestCreated(false)
        setIsLoading(true)
        const messages = await validateFormData(partnerFormData.formData, formKey)
        if (messages.length === 0) {
          const result = await onApply(partnerFormData)
          if (result === UpdatePartnerResult.Updated) {
            setIsUpdateSuccessful(true)
          }
          if (result === UpdatePartnerResult.Requested) {
            setIsRequestCreated(true)
          }
          setIsCurrentMenuItemEditable(false)
        }
        setIsLoading(false)
      }
    }, [formKey, onApply, partnerFormData, setIsCurrentMenuItemEditable])

    const discardChanges = useCallback(() => {
      if (onCancel) {
        onCancel(partnerFormData)
        setIsCurrentMenuItemEditable(false)
      }
    }, [onCancel, partnerFormData, setIsCurrentMenuItemEditable])

    const {
      handleOnMandatoryAttachmentsConfirm,
      handleOnMandatoryAttachmentsReject,
      hasMandatoryAttachments,
      isMandatoryAttachmentsDialogVisible,
      setIsMandatoryAttachmentsDialogVisible,
      mandatoryAttachmentsContent,
    } = useMandatoryAttachmentsForm({
      onConfirm: applyChanges,
    })
    const headerHeight = `${PARTNER_CONTAINER_HEADER_HEIGHT}px`

    const preparedTitle = useMemo(() => {
      if (typeof title === 'string') {
        return <Translation text={title} />
      }
      return title
    }, [title])

    /** ---------------------- Handlers ------------------------ */

    const handleOnPartnerInactivateConfirm = useCallback(() => {
      if (!isReadOnly) {
        setIsInactivatePartnerDialogVisible(false)
        setIsLoading(true)
        inactivatePartner().finally(() => {
          setIsLoading(false)
        })
      }
    }, [inactivatePartner, isReadOnly])

    const handleOnPartnerInactivateReject = useCallback(() => {
      setIsInactivatePartnerDialogVisible(false)
    }, [])

    const handleOnInactiveClick = useCallback(() => {
      setIsInactivatePartnerDialogVisible(true)
    }, [])

    const handleOnEditClick = useCallback(() => {
      setIsCurrentMenuItemEditable(true)
    }, [setIsCurrentMenuItemEditable])

    const handleOnApplyClick = useCallback(() => {
      if (isAttachmentRequired) {
        setIsMandatoryAttachmentsDialogVisible(true)
      } else {
        applyChanges()
      }
    }, [applyChanges, isAttachmentRequired, setIsMandatoryAttachmentsDialogVisible])

    const handleOnCancelClick = useCallback(() => {
      discardChanges()
    }, [discardChanges])

    /** ---------------------- Renderer ------------------------ */

    const renderWorkflowStatusReadOnlyTooltip = useCallback(
      () => (
        <Translation text={partnerEditorTranslation.container.tips.workflowStatusReadOnly} span />
      ),
      []
    )

    /** ---------------------- Effects ------------------------ */

    useEffect(() => {
      return () => {
        if (isUpdateSuccessful) {
          setIsUpdateSuccessful(false)
        }
      }
    }, [isUpdateSuccessful, currentMenuItem, isCurrentMenuItemEditable])

    return (
      <div className={styles.container}>
        <LoaderOverlay isVisible={isLoading} color="white" />
        <div className={styles.headerWrapper}>
          <header style={{ height: headerHeight }} className={styles.header}>
            <PartnerFormTitle title={preparedTitle} partner={partner} />
            <div className={styles.actions}>
              <RenderWhen condition={!isInactive && !isCurrentMenuItemEditable} keepMounted>
                <Tooltip
                  disabled={isPartnerInactivationPossible}
                  tipContent={() => <>{partnerInactivationImpossibleMessage}</>}
                >
                  <Button
                    disabled={!isPartnerInactivationPossible || isReadOnly}
                    onClick={handleOnInactiveClick}
                  >
                    <Translation text={partnerEditorTranslation.container.button.inactivate} />
                  </Button>
                </Tooltip>
              </RenderWhen>
              <RenderWhen
                condition={!isCurrentMenuItemEditable && !isReadOnly && !isDisabled}
                keepMounted
              >
                <Tooltip
                  disabled={!isWorkflowStatusReadOnly}
                  tipContent={renderWorkflowStatusReadOnlyTooltip}
                >
                  <Button onClick={handleOnEditClick} disabled={isWorkflowStatusReadOnly}>
                    <Translation text={partnerEditorTranslation.container.button.edit} />
                  </Button>
                </Tooltip>
              </RenderWhen>
              <RenderWhen condition={isCurrentMenuItemEditable} keepMounted>
                <Button
                  disabled={partnerFormData.modifiedFields.length === 0 || !partnerFormData.isValid}
                  onClick={handleOnApplyClick}
                >
                  <Translation text={partnerEditorTranslation.container.button.apply} />
                </Button>
              </RenderWhen>
              <RenderWhen condition={isCurrentMenuItemEditable} keepMounted>
                <Button color="danger" onClick={handleOnCancelClick}>
                  <Translation text={partnerEditorTranslation.container.button.cancel} />
                </Button>
              </RenderWhen>
            </div>
          </header>
          <div className={styles.infoBlock}>
            <RenderWhen condition={isInactive}>
              <PartnerInactivePanel />
            </RenderWhen>
            <RenderWhen condition={isUpdateSuccessful}>
              <InfoBlock
                text={partnerEditorTranslation.warning.successfulUpdate}
                type={InfoBlockType.Success}
                hasCloseButton
              />
            </RenderWhen>
            <RenderWhen condition={isRequestCreated}>
              <InfoBlock
                text={partnerEditorTranslation.warning.requestCreated}
                type={InfoBlockType.Help}
                hasCloseButton
              />
            </RenderWhen>
            <RenderWhen condition={Boolean(errorMessages?.length)}>
              <InfoBlock text={formattedErrorMessages} type={InfoBlockType.Danger} hasCloseButton />
            </RenderWhen>
            <RenderWhen condition={Boolean(partnerInactivationErrorMessages.length)}>
              <InfoBlock
                text={formattedInactivationErrorMessages}
                type={InfoBlockType.Danger}
                hasCloseButton
              />
            </RenderWhen>
            <RenderWhen condition={!isInactive}>
              <PartnerWorkflowStatusPanel partner={partner} />
            </RenderWhen>
          </div>
        </div>
        <div className={styles.content}>{children}</div>
        <ConfirmDialog
          title={partnerEditorTranslation.dialog.mandatoryAttachments.title}
          confirmBtnText={partnerEditorTranslation.dialog.mandatoryAttachments.confirmBtn}
          rejectBtnText={partnerEditorTranslation.dialog.mandatoryAttachments.rejectBtn}
          isVisible={isMandatoryAttachmentsDialogVisible}
          content={mandatoryAttachmentsContent}
          onConfirm={handleOnMandatoryAttachmentsConfirm}
          onReject={handleOnMandatoryAttachmentsReject}
          isApplyDisabled={!hasMandatoryAttachments}
        />
        <ConfirmDialog
          title={partnerEditorTranslation.dialog.inactivatePartner.title}
          confirmBtnText={partnerEditorTranslation.dialog.inactivatePartner.confirmBtn}
          rejectBtnText={partnerEditorTranslation.dialog.inactivatePartner.rejectBtn}
          isVisible={isInactivatePartnerDialogVisible}
          content={partnerEditorTranslation.dialog.inactivatePartner.content}
          onConfirm={handleOnPartnerInactivateConfirm}
          onReject={handleOnPartnerInactivateReject}
        />
      </div>
    )
  }
)
